import React, { forwardRef } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { useViewport } from '../../../hooks/useViewport';
import { SearchField } from '../../mobile/molecules/SearchField';
import { SearchClear } from '../../shells/SearchBox';

/**
 * Toolbar, the band above a data table.
 *
 * CSS: `desktop/system.css` `.dsk-toolbar`, plus `.dsk-search`, `.dsk-chip`,
 * `.dsk-hint` and `.dsk-segmented`.
 * Behaviour: none of the numbered reference behaviours. Deliberately NOT
 * behaviour #6: an in-page filter field carries no `.dsk-kbd` shortcut chip,
 * which is exactly how the reference tells the global search panel to leave it
 * alone. Only the nav search opens a `SearchPanel`.
 *
 * Anatomy: a search field, caller-supplied filter slots, a result count, and a
 * second `.dsk-toolbar` row of applied-filter chips where each chip removes
 * itself. A chip IS the remove control, so there is no hover-only affordance to
 * make keyboard reachable.
 *
 * NO DENSITY TOGGLE. It used to sit in the trailing cluster on every console
 * table. Rows are the comfortable 48px everywhere now; see `Table.jsx` for why
 * the compact half was never worth the second set of cell renderers it needed.
 *
 * OVERFLOW. `.dsk-table-wrap` is `overflow: hidden`, and `.dsk-toolbar` does
 * not yet wrap, so a crowded toolbar has its trailing controls sliced off at
 * 1024px. `flex-wrap: wrap` is landing upstream in `desktop/system.css`; this
 * component adds no `overflow` of its own and assumes nothing about its
 * children fitting on one line, so it picks that fix up for free. The trailing
 * cluster wraps internally for the same reason.
 *
 * DISABLED. Every control here passes the real `disabled` attribute rather than
 * a visual class, so the `.dsk-btn--ghost:disabled` rule landing upstream also
 * arrives for free, and keyboard users cannot reach a control that looks off.
 *
 * THE SEARCH FIELD PICKS A TRACK, like `Drawer` picks `Sheet`.
 * ----------------------------------------------------------
 * `.dsk-search` rests at `border: 1px solid transparent` and only reveals its
 * hairline on `:hover` (`desktop/system.css`). Above the handoff that is fine:
 * the toolbar sits on a white `.dsk-table-wrap`, so the field's `--bg-secondary`
 * fill reads as a field on its own. BELOW the handoff there is no card behind
 * it -- the toolbar sits straight on the page, whose background IS
 * `--bg-secondary` -- and a phone has no hover to summon the border, so the
 * field rendered as a bare glyph and a placeholder floating on the page. That
 * is what "missing border" on the dashboard's Collection and Order history
 * tabs was.
 *
 * The mobile track already owns the right control: `.ds-search` is a 44px
 * label-wrapped pill filled `--bg-tertiary`, one step darker than the page, so
 * it reads as a field by fill rather than by a hover-only border, and it hits
 * the 44px tap target `.dsk-search`'s 40px `--dsk-control-h` misses. So below
 * the handoff this MOUNTS THAT, one tree only. `SellerStore.view` and
 * `pages/Sets/tiles` already choose per track exactly this way; `Toolbar` was
 * the last in-page search field in the app still hardcoding the desktop pill
 * onto both.
 *
 * No clear button on that branch, and that is deliberate twice over: `.ds-search`
 * is a `<label>`, and a button inside a label is activated twice by one tap (the
 * same trap the desktop branch below documents), and no other mobile search
 * surface in the app carries one either. The recovery path that matters is
 * already there -- a filter that returns nothing renders `ConsoleEmpty` with an
 * explicit clear action.
 */

/* Layout only, never a themeable value: this pushes the trailing cluster right
   the same way `.dsk-nav-icons` and `.dsk-utilitybar__end` do in the layer. */
const TRAILING = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  minWidth: 0,
};

/* Layout only, same rule as TRAILING above. Below the handoff the search takes
   the WHOLE first row and the filters and count wrap beneath it. `.dsk-toolbar`
   is `flex-wrap: wrap`, so without a basis the three of them fight over 390px
   and the field shrinks until its own placeholder is clipped -- measured on
   Order history, which carries a status select as well: "Filter by order or
   card" rendered as "Filter by orde". A phone has the vertical room and does
   not have the horizontal room, so the field gets the row. */
const MOBILE_SEARCH_ROW = {
  flexBasis: '100%',
  minWidth: 0,
  display: 'flex',
};

/**
 * FilterChip, one applied filter. CSS: `.dsk-chip`.
 * The whole chip is the remove button, so the accessible name says so rather
 * than reading as a bare value.
 */
export const FilterChip = forwardRef(function FilterChip(
  { label, onRemove, removeLabel, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn('dsk-chip', className)}
      onClick={onRemove}
      aria-label={removeLabel || `Remove filter: ${label}`}
      {...rest}
    >
      {label}
      <Icon as={X} size="sm" />
    </button>
  );
});

export const Toolbar = forwardRef(function Toolbar(
  {
    search,
    onSearchChange,
    searchPlaceholder = 'Search',
    searchLabel = 'Search',
    filters,
    chips = [],
    onClearFilters,
    clearLabel = 'Clear all',
    count,
    children,
    className,
    ...rest
  },
  ref,
) {
  const { isDesktop } = useViewport();
  const hasSearch = typeof onSearchChange === 'function';
  const hasTrailing = count != null;

  const searchField = isDesktop ? (
    /* A DIV, not a LABEL. The clear button is a real button and a button
       inside a <label> is activated twice by one click, once as itself
       and once as the label's proxy for the input. The accessible name
       moves onto the input as `aria-label`, which says the same thing
       without the wrapper. */
    <div className="dsk-search">
      <Icon as={MagnifyingGlass} />
      <input
        type="search"
        aria-label={searchLabel}
        value={search == null ? '' : search}
        placeholder={searchPlaceholder}
        autoComplete="off"
        onChange={(event) => onSearchChange(event.target.value)}
      />
      {search ? <SearchClear onClear={() => onSearchChange('')} /> : null}
    </div>
  ) : (
    <div style={MOBILE_SEARCH_ROW}>
      <SearchField
        aria-label={searchLabel}
        value={search == null ? '' : search}
        placeholder={searchPlaceholder}
        autoComplete="off"
        onChange={(event) => onSearchChange(event.target.value)}
      />
    </div>
  );

  return (
    <>
      <div ref={ref} className={cn('dsk-toolbar', className)} {...rest}>
        {hasSearch ? searchField : null}

        {filters}
        {children}

        {hasTrailing ? (
          <div style={TRAILING}>
            <span className="dsk-hint">{count}</span>
          </div>
        ) : null}
      </div>

      {chips.length > 0 ? (
        <div className="dsk-toolbar" role="group" aria-label="Applied filters">
          {chips.map((chip) => (
            <FilterChip
              key={chip.id != null ? chip.id : chip.label}
              label={chip.label}
              removeLabel={chip.removeLabel}
              onRemove={chip.onRemove}
            />
          ))}
          {typeof onClearFilters === 'function' ? (
            <button type="button" className="dsk-btn dsk-btn--ghost dsk-btn--sm" onClick={onClearFilters}>
              {clearLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
});

export default Toolbar;
